import {
  routes,
  TEXT_STRING,
  IdSample,
  IdBackSample,
  IdSelfieSample,
} from '../../constants';
import {
  EDeferralRequestType,
  TDeferralPaymentRequestBody,
  TPhoneInput,
  TResPendingPayment,
} from 'types';
import { useCallback, useMemo, useState } from 'react';
import {
  Button,
  CameraCaptureCard,
  Text,
  Icon,
  TextField,
  PhoneInput,
  ModalSuccess,
} from 'components';
import { useUploadFile } from 'hooks';
import { convertFormatAmount, validatePhone, getFormattedAmount } from 'utils';
import { createDeferralPaymentRequest } from 'api';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Country } from 'react-phone-number-input';
import { useUserStore } from 'store';

const countryCode = (process.env.REACT_APP_COUNTRY_CODE || 'JP') as Country;

const deferredPaymentReqStr = TEXT_STRING.PAYMENT_DEFERRAL_REQUEST;
const paymentStr = TEXT_STRING.PAYMENT;
const commonStr = TEXT_STRING.COMMON;

type Props = {
  paymentPendingData: TResPendingPayment;
  amount: number;
  remainingAmount: number;
  type: EDeferralRequestType;
  onChangeAmount: (amount: number) => void;
  className?: string;
};

const DeferralPaymentRequest = ({
  paymentPendingData,
  amount,
  remainingAmount,
  type,
  onChangeAmount,
  className = '',
}: Props): JSX.Element => {
  // Hooks
  const navigate = useNavigate();
  const { uploadToS3 } = useUploadFile();
  const { setUserRefreshRequested } = useUserStore();

  // States
  const [openModal, setOpenModal] = useState(false);
  const [phone, setPhone] = useState<TPhoneInput>({
    countryCode,
    number: '',
  });
  const [idImage, setIdImage] = useState<File | null>(null);
  const [idBackImage, setIdBackImage] = useState<File | null>(null);
  const [idSelfieImage, setIdSelfieImage] = useState<File | null>(null);
  const [isFocus, setIsFocus] = useState(false);

  // Memo, Callbacks
  const remaining = useMemo(
    () => remainingAmount - +amount,
    [amount, remainingAmount],
  );

  const announceDescriptions = useMemo(() => {
    const list = deferredPaymentReqStr.ANNOUNCE_DESCRIPTION;

    if (type === EDeferralRequestType.BANK) {
      return list.filter((_, index) => index !== 2);
    }

    return list;
  }, [type]);

  const { projectId, id: paymentId } = paymentPendingData;

  const { icon, typeName, title, expireDate, successMessage } = useMemo(() => {
    const isPostpay = type === EDeferralRequestType.POSTPAY;

    const typeName =
      type === EDeferralRequestType.POSTPAY
        ? deferredPaymentReqStr.POSTPAY
        : deferredPaymentReqStr.BANK;

    const title = deferredPaymentReqStr.TITLE.replace('$type', typeName);

    const expireDate = isPostpay
      ? deferredPaymentReqStr.POSTPAY_EXPIRE_DATE
      : deferredPaymentReqStr.BANK_EXPIRE_DATE;

    const successMessage = isPostpay
      ? deferredPaymentReqStr.POSTPAY_SUCCESS_MESSAGE
      : deferredPaymentReqStr.BANK_SUCCESS_MESSAGE;

    return {
      icon: isPostpay ? ('postpay' as const) : ('bank' as const),
      typeName,
      title,
      expireDate,
      successMessage,
    };
  }, [type]);

  const formatTemplate = useCallback(
    (text: string) =>
      text
        .replace('$type', typeName)
        .replace('$expireDate', expireDate)
        .replace('$title', title),
    [typeName, expireDate, title],
  );

  const { mutateAsync, isPending, data } = useMutation({
    mutationFn: (body: TDeferralPaymentRequestBody) =>
      createDeferralPaymentRequest(body),
    onSuccess() {
      setOpenModal(true);
      setUserRefreshRequested(true);
    },
    onError(err: any) {
      // TODO: error handling
      console.error(err);
    },
  });

  const handleSubmit = useCallback(async () => {
    if (!idImage || !idBackImage || !idSelfieImage) return;

    const [
      { uploadedUrl: idImageUrl },
      { uploadedUrl: idBackImageUrl },
      { uploadedUrl: idSelfieImageUrl },
    ] = await uploadToS3({
      files: [idImage, idBackImage, idSelfieImage],
    });

    const body: TDeferralPaymentRequestBody = {
      projectId,
      paymentId,
      phone: phone.number ?? '',
      amount,
      idImageUrl,
      idBackImageUrl,
      idSelfieImageUrl,
      type,
    };

    await mutateAsync(body);
  }, [
    amount,
    idBackImage,
    idImage,
    idSelfieImage,
    mutateAsync,
    projectId,
    paymentId,
    phone.number,
    type,
    uploadToS3,
  ]);

  return (
    <div className={`${className} flex flex-col gap-4`}>
      {/* Title */}
      <div className="flex items-center gap-2">
        <Icon icon={icon} width={21} />
        <Text
          fontWeight="font-medium"
          className="text-base 2xs:text-lg xs:text-xl"
        >
          {title}
        </Text>
      </div>

      {/* Announce */}
      <div className="flex flex-col gap-2 my-2">
        <Text fontWeight="font-semibold" className="text-base tracking-wide">
          {deferredPaymentReqStr.ANNOUNCE_TITLE}
        </Text>
        <div className="flex flex-col gap-2">
          {announceDescriptions.map(({ HEAD, DESC }, index) => (
            <div className="flex flex-col gap-1" key={HEAD}>
              <Text fontWeight="font-bold" className="text-sm tracking-wide">
                {formatTemplate(HEAD.replace('$index', `${index + 1}`))}
              </Text>
              <ul className="flex flex-col gap-2 list-none m-0 p-0">
                {DESC.map((line, i) => (
                  <li
                    className="text-sm relative pl-5 before:content-['・'] before:absolute before:left-0"
                    key={i}
                  >
                    {formatTemplate(line)}
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <Text fontWeight="font-bold" className="text-sm tracking-wide mt-2">
            {formatTemplate(deferredPaymentReqStr.ANNOUNCE_AGREE_NOTE)}
          </Text>
        </div>
      </div>

      {/* Amount section */}
      <div className="border-y flex flex-col gap-5 py-8">
        <div>
          <Text bold center className="text-sm">
            {paymentStr.ENTER_AMOUNT}
          </Text>
          <Text bold center className="text-3xl">
            {getFormattedAmount(remainingAmount, commonStr.YEN)}
          </Text>
        </div>

        <div className="flex items-center gap-4">
          <TextField
            name="amount"
            value={
              isFocus
                ? convertFormatAmount(amount)
                : getFormattedAmount(+amount)
            }
            onChange={(e) => onChangeAmount(+e.target.value)}
            onFocus={() => setIsFocus(true)}
            onBlur={() => setIsFocus(true)}
            type="tel"
            inputClass="py-4"
          />
          <div className="pr-1 text-2xl font-bold">
            <span>{commonStr.YEN}</span>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <div className="flex gap-4 items-center">
            <Text className="text-sm 2xs:text-base xs:text-lg">
              {paymentStr.REMAINING_BALANCE}
            </Text>
            <Text
              bold
              className="text-sm 2xs:text-base xs:text-lg flex items-center"
            >
              {getFormattedAmount(remaining, commonStr.YEN)}
            </Text>
          </div>
        </div>
      </div>

      {/* Capture cards */}
      <CameraCaptureCard
        title={deferredPaymentReqStr.ID_CAPTURE_TITLE}
        label={deferredPaymentReqStr.ID_CAPTURE}
        sampleSrc={IdSample}
        capture="environment"
        name="idImage"
        onCapture={setIdImage}
      />
      <CameraCaptureCard
        title={deferredPaymentReqStr.ID_BACK_CAPTURE_TITLE}
        label={deferredPaymentReqStr.ID_BACK_CAPTURE}
        sampleSrc={IdBackSample}
        capture="environment"
        name="idBackImage"
        onCapture={setIdBackImage}
      />
      <CameraCaptureCard
        title={deferredPaymentReqStr.ID_SELFIE_TITLE}
        label={deferredPaymentReqStr.ID_SELFIE}
        sampleSrc={IdSelfieSample}
        capture="user"
        name="idSelfieImage"
        onCapture={setIdSelfieImage}
      />

      {/* Phone */}
      <div className="flex flex-col gap-2">
        <Text
          fontWeight="font-medium"
          className="text-xs 2xs:text-sm xs:text-base"
        >
          {deferredPaymentReqStr.PHONE_TITLE}
        </Text>
        <PhoneInput
          name="phone"
          value={phone}
          onChange={setPhone}
          placeholder="010 1234 5678"
          className="mb-16px"
          error={!validatePhone(phone)}
        />
      </div>

      {/* Submit */}
      <Button
        onClick={handleSubmit}
        disabled={
          !phone.number ||
          !validatePhone(phone) ||
          amount === 0 ||
          !idImage ||
          !idSelfieImage ||
          isPending
        }
        loading={isPending}
        className="mt-2"
      >
        {deferredPaymentReqStr.SEND}
      </Button>

      {/* Success modal */}
      <ModalSuccess
        open={openModal}
        onConfirm={() => {
          setOpenModal(false);
          navigate(routes.CONVERSATION.replace(':id', data?.channelId || ''));
        }}
      >
        <Text className="mt-6" center textColor="text-neutral-900">
          {successMessage}
        </Text>
      </ModalSuccess>
    </div>
  );
};

export default DeferralPaymentRequest;
